import { FormEvent, useEffect, useState } from 'react'
import { partnerApi } from '@/services/api/partner'
import type { School, CountryInfo, RegionInfo } from '@/services/api/ultraadmin'
import { extractErrorMessage } from '@/utils/errorHandler'

export function PartnerSchoolsPage() {
  const [schools, setSchools] = useState<School[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<School | null>(null)
  const [confirmName, setConfirmName] = useState('')
  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [phone, setPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [country, setCountry] = useState('CH')
  const [countries, setCountries] = useState<CountryInfo[]>([])
  const [regions, setRegions] = useState<RegionInfo[]>([])
  const [regionId, setRegionId] = useState('')
  const [billingCurrency, setBillingCurrency] = useState('CHF')
  const [timezone, setTimezone] = useState('Europe/Zurich')
  const [editTarget, setEditTarget] = useState<School | null>(null)
  const [editName, setEditName] = useState('')
  const [editShort, setEditShort] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editAddress, setEditAddress] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editZip, setEditZip] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [domainSchool, setDomainSchool] = useState<School | null>(null)
  const [domainValue, setDomainValue] = useState('')

  const load = async () => {
    try {
      setSchools(await partnerApi.listSchools())
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Schulen konnten nicht geladen werden' }))
    }
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    partnerApi.getCountries().then(setCountries).catch(() => undefined)
  }, [])
  useEffect(() => {
    if (!country) return
    partnerApi.getRegions(country).then(setRegions).catch(() => setRegions([]))
  }, [country])

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await partnerApi.createSchool({
        name,
        short_name: shortName,
        email: email || undefined,
        address: address || undefined,
        city: city || undefined,
        zip_code: zipCode || undefined,
        phone: phone || undefined,
        website: website || undefined,
        country,
        region_id: regionId || undefined,
        language: 'de',
        billing_currency: billingCurrency,
        timezone,
      })
      setShowCreate(false)
      setName(''); setShortName(''); setEmail(''); setAddress(''); setCity(''); setZipCode(''); setPhone(''); setWebsite('')
      await load()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Anlegen fehlgeschlagen' }))
    }
  }

  const onUnlink = async (e: FormEvent) => {
    e.preventDefault()
    if (!unlinkTarget) return
    try {
      await partnerApi.unlinkSchool(unlinkTarget.id, confirmName)
      setUnlinkTarget(null)
      setConfirmName('')
      await load()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Sperren fehlgeschlagen' }))
    }
  }

  const openEdit = (s: School) => {
    setEditTarget(s)
    setEditName(s.name)
    setEditShort(s.short_name || '')
    setEditEmail(s.email || '')
    setEditAddress(s.address || '')
    setEditCity(s.city || '')
    setEditZip(s.zip_code || '')
    setEditPhone(s.phone || '')
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Portfolio</h1>
        <button type="button" onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg">+ Schule</button>
      </div>
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      <table className="min-w-full bg-white rounded-lg shadow">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Name</th>
            <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Status</th>
            <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Domains</th>
            <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Hinweis</th>
            <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {schools.map((s) => (
            <tr key={s.id} className="border-t">
              <td className="px-4 py-2">{s.name}</td>
              <td className="px-4 py-2">{s.is_active ? 'aktiv' : 'deaktiviert'}</td>
              <td className="px-4 py-2">{(s.domains || []).map((d) => d.domain).join(', ')}</td>
              <td className="px-4 py-2 text-xs text-gray-500">{s.staff_card_hint || '—'}</td>
              <td className="px-4 py-2">
                <button type="button" className="text-blue-700 text-sm mr-2" onClick={() => openEdit(s)}>Stammdaten</button>
                <button type="button" className="text-blue-700 text-sm mr-2" onClick={() => { setDomainSchool(s); setDomainValue('') }}>Domain</button>
                <button type="button" className="text-red-700 text-sm" onClick={() => { setUnlinkTarget(s); setConfirmName('') }}>Sperren</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <form onSubmit={onCreate} className="bg-white p-6 rounded-lg w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold">Schule anlegen</h2>
            <input className="border w-full p-2" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <input className="border w-full p-2" placeholder="Kurzname" value={shortName} onChange={(e) => setShortName(e.target.value)} required />
            <input className="border w-full p-2" placeholder="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input className="border w-full p-2" placeholder="Adresse" value={address} onChange={(e) => setAddress(e.target.value)} />
            <input className="border w-full p-2" placeholder="PLZ" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
            <input className="border w-full p-2" placeholder="Ort" value={city} onChange={(e) => setCity(e.target.value)} />
            <input className="border w-full p-2" placeholder="Telefon" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <input className="border w-full p-2" placeholder="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
            <input className="border w-full p-2" placeholder="Zeitzone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            <label className="block text-sm">Währung (unveränderlich)
              <select className="border w-full p-2" value={billingCurrency} onChange={(e) => setBillingCurrency(e.target.value)} required>
                <option value="CHF">CHF</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            <select className="border w-full p-2" value={country} onChange={(e) => setCountry(e.target.value)}>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <select className="border w-full p-2" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
              <option value="">Region</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowCreate(false)}>Abbrechen</button>
              <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Anlegen</button>
            </div>
          </form>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <form className="bg-white p-6 rounded-lg w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onSubmit={async (e) => {
            e.preventDefault()
            try {
              await partnerApi.updateSchool(editTarget.id, {
                name: editName,
                short_name: editShort || editName.slice(0, 8),
                email: editEmail || undefined,
                address: editAddress || undefined,
                city: editCity || undefined,
                zip_code: editZip || undefined,
                phone: editPhone || undefined,
              })
              setEditTarget(null)
              await load()
            } catch (err: unknown) {
              setError(extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' }))
            }
          }}>
            <h2 className="font-bold">Stammdaten</h2>
            <input className="border w-full p-2" placeholder="Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <input className="border w-full p-2" placeholder="Kurzname" value={editShort} onChange={(e) => setEditShort(e.target.value)} />
            <input className="border w-full p-2" placeholder="E-Mail" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            <input className="border w-full p-2" placeholder="Adresse" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
            <input className="border w-full p-2" placeholder="PLZ" value={editZip} onChange={(e) => setEditZip(e.target.value)} />
            <input className="border w-full p-2" placeholder="Ort" value={editCity} onChange={(e) => setEditCity(e.target.value)} />
            <input className="border w-full p-2" placeholder="Telefon" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditTarget(null)}>Abbrechen</button>
              <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Speichern</button>
            </div>
          </form>
        </div>
      )}
      {domainSchool && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <form className="bg-white p-6 rounded-lg w-full max-w-md space-y-3" onSubmit={async (e) => {
            e.preventDefault()
            try {
              await partnerApi.addDomain(domainSchool.id, domainValue, false)
              setDomainSchool(null)
              await load()
            } catch (err: unknown) {
              setError(extractErrorMessage(err, { loadError: 'Domain fehlgeschlagen' }))
            }
          }}>
            <h2 className="font-bold">Domain: {domainSchool.name}</h2>
            <ul className="text-sm space-y-1">
              {(domainSchool.domains || []).map((d) => (
                <li key={d.id} className="flex justify-between gap-2">
                  <span>{d.domain}{d.is_primary ? ' (primär)' : ''}</span>
                  <button type="button" className="text-red-700" onClick={async () => {
                    try {
                      await partnerApi.removeDomain(domainSchool.id, d.id)
                      await load()
                      setDomainSchool((prev) => prev ? { ...prev, domains: (prev.domains || []).filter((x) => x.id !== d.id) } : prev)
                    } catch (err: unknown) {
                      setError(extractErrorMessage(err, { loadError: 'Domain entfernen fehlgeschlagen' }))
                    }
                  }}>Entfernen</button>
                </li>
              ))}
            </ul>
            <input className="border w-full p-2" placeholder="schule.ch" value={domainValue} onChange={(e) => setDomainValue(e.target.value)} required />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDomainSchool(null)}>Abbrechen</button>
              <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded">Hinzufügen</button>
            </div>
          </form>
        </div>
      )}

      {unlinkTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <form onSubmit={onUnlink} className="bg-white p-6 rounded-lg w-full max-w-md space-y-3">
            <h2 className="font-bold">Sperren</h2>
            <p className="text-sm text-gray-700">
              Die Schule wird an die Plattform übergeben und bleibt nutzbar, solange sie zahlt.
              Superadmins bleiben. Es wird nichts gelöscht. Rückgängig machen kann nur der Ultraadmin.
            </p>
            <p className="text-sm">Bitte Schulname <strong>{unlinkTarget.name}</strong> abtippen:</p>
            <input className="border w-full p-2" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setUnlinkTarget(null)}>Abbrechen</button>
              <button type="submit" className="bg-red-600 text-white px-3 py-1 rounded">Sperren</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
